import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["kind", "type", "add", "rounded", "icon", "compact", "disabled", "children", "style", "loading", "className", "tabIndex", "role", "onFocus", "onBlur", "onClick", "ariaLabel"];
import React, { forwardRef, useContext } from 'react';
import classnames from 'classnames';
import { KEY_VALUES } from '../utils';
import { ButtonGroupContext } from './ButtonGroupContext';
import useKeyBoardFocus from '../utils/useKeyboardFocus';
import { useLocalization } from "@appkit4/react-api";
var Button = /*#__PURE__*/forwardRef(function (props, ref) {
  var _useContext = useContext(ButtonGroupContext),
    _useContext$disabled = _useContext.disabled,
    disabledContext = _useContext$disabled === void 0 ? false : _useContext$disabled,
    _useContext$compact = _useContext.compact,
    compactContext = _useContext$compact === void 0 ? false : _useContext$compact,
    _useContext$kind = _useContext.kind,
    kindContext = _useContext$kind === void 0 ? 'primary' : _useContext$kind;
  var _useLocalization = useLocalization(),
    t = _useLocalization.t;
  var _props$kind = props.kind,
    kind = _props$kind === void 0 ? kindContext : _props$kind,
    _props$type = props.type,
    type = _props$type === void 0 ? 'button' : _props$type,
    _props$add = props.add,
    add = _props$add === void 0 ? false : _props$add,
    _props$rounded = props.rounded,
    rounded = _props$rounded === void 0 ? false : _props$rounded,
    _props$icon = props.icon,
    icon = _props$icon === void 0 ? '' : _props$icon,
    _props$compact = props.compact,
    compact = _props$compact === void 0 ? compactContext : _props$compact,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? disabledContext : _props$disabled,
    _props$children = props.children,
    children = _props$children === void 0 ? '' : _props$children,
    style = props.style,
    _props$loading = props.loading,
    loading = _props$loading === void 0 ? false : _props$loading,
    className = props.className,
    _props$tabIndex = props.tabIndex,
    tabIndex = _props$tabIndex === void 0 ? 0 : _props$tabIndex,
    _props$role = props.role,
    role = _props$role === void 0 ? 'button' : _props$role,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    onClick = props.onClick,
    ariaLabel = props.ariaLabel,
    others = _objectWithoutPropertiesLoose(props, _excluded);
  var divRef = React.useRef(null);
  var isLoadingBtn = ('loading' in props);
  useKeyBoardFocus();
  var handleClick = function handleClick(event) {
    if (loading) {
      return;
    }
    onClick === null || onClick === void 0 ? void 0 : onClick(event);
  };
  var handleButtonFocus = function handleButtonFocus(event) {
    if (loading) {
      return;
    }
    onFocus && onFocus(event);
  };
  var handleButtonBlur = function handleButtonBlur(event) {
    if (loading) {
      return;
    }
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
  };
  var handleKeyDown = function handleKeyDown(event) {
    if (event.key === KEY_VALUES.ENTER && type === 'submit') {
      // Find the closest form element and submit it  
      var form = event.target.closest('form');
      if (form) {
        form.submit();
      }
    }
    if (event.key === KEY_VALUES.ENTER || event.key === KEY_VALUES.SPACE) {
      event.preventDefault();
      onClick === null || onClick === void 0 ? void 0 : onClick(event);
    }
  };
  var classes = classnames('ap-button', "ap-button-" + kind, className, {
    'ap-button-rounded': add || rounded,
    'ap-button-rounded-text': rounded && children,
    'ap-button-icon': add || icon,
    'ap-button-compact': compact,
    'ap-button-loading': !add && isLoadingBtn,
    'in-loading': loading
  });
  var classNameIcon = classnames('Appkit4-icon', icon, {
    'icon-plus-outline': add && !icon
  });
  var classNameLoading = classnames('ap-button-label', {
    hidden: isLoadingBtn && loading
  });
  var classNameAnimation = classnames('ap-button-loading-icon', {
    hidden: !loading,
    'loading-animation': loading
  });
  return /*#__PURE__*/React.createElement("button", _extends({
    ref: ref,
    className: classes,
    style: style,
    disabled: disabled,
    onFocus: handleButtonFocus,
    onBlur: handleButtonBlur,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    "aria-disabled": disabled,
    "aria-label": ariaLabel ? ariaLabel : add ? t("aria").buttonAddLabel : undefined,
    tabIndex: loading ? -1 : tabIndex,
    role: role,
    type: type
  }, others), isLoadingBtn ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: loading ? 'label-hidden' : classNameLoading
  }, icon && /*#__PURE__*/React.createElement("span", {
    className: classNameIcon,
    "aria-hidden": loading ? false : true
  }), children && /*#__PURE__*/React.createElement("span", {
    className: "ap-font-medium",
    "aria-hidden": loading ? false : true
  }, children)), /*#__PURE__*/React.createElement("span", {
    ref: divRef,
    className: classNameAnimation
  })) : add ? /*#__PURE__*/React.createElement("span", {
    className: classNameIcon
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, icon && /*#__PURE__*/React.createElement("span", {
    className: classNameIcon,
    "aria-hidden": true
  }), children && /*#__PURE__*/React.createElement("span", {
    className: "ap-button-label ap-font-medium"
  }, children)));
});
Button.defaultProps = {
  kind: 'primary'
};
export default Button;